/**
 * plans.ts — Gói dịch vụ phía giao diện.
 *
 * GIÁ, HẠN MỨC, MỐC THỜI HẠN không nằm ở đây: nguồn duy nhất là bảng giá super-admin đặt
 * (Platform → Bảng giá gói), đọc qua GET /billing/plans; số tiền đơn hàng do server tính
 * (POST /billing/quote — cùng hàm với checkout). File này chỉ giữ phần HIỂN THỊ (tính năng,
 * biểu tượng, màu thẻ) và vài hàm ước tính thuần.
 *
 * ── Ánh xạ nhãn `organization.plan` → gói (không phân biệt hoa thường, bỏ dấu) ──
 *   null / rỗng                                   → free        (Miễn phí)
 *   free, mien phi, trial, dung thu               → free
 *   starter, basic, co ban                        → starter     (Cơ bản)
 *   pro, professional, chuyen nghiep              → pro         (Chuyên nghiệp)
 *   enterprise, business, doanh nghiep, custom    → enterprise  (Doanh nghiệp)
 *   trùng mã một gói trong bảng giá               → gói đó
 *   nhãn khác (vd "Gói Trà Dược 2026")            → 'custom': hiện nguyên nhãn
 * (Bảng này trùng với resolvePlanId phía server.)
 */
import type { BillingOrder, PricingConfig, PricingPlan, PurchaseMode, QuotaKey, Quotas, Quote } from '@/hooks/use-billing'
import { i18n } from '@/lib/i18n'

export const QUOTA_KEYS: QuotaKey[] = ['members', 'officialChannels', 'personalChannels']

export const QUOTA_META: Record<QuotaKey, { label: string; unit: string; short: string; hint: string }> = {
  get members() { return { label: i18n.t('billing:quotaMeta.members.label'), unit: i18n.t('billing:quotaMeta.members.unit'), short: i18n.t('billing:quotaMeta.members.short'), hint: i18n.t('billing:quotaMeta.members.hint') } },
  get officialChannels() { return { label: i18n.t('billing:quotaMeta.officialChannels.label'), unit: i18n.t('billing:quotaMeta.officialChannels.unit'), short: i18n.t('billing:quotaMeta.officialChannels.short'), hint: i18n.t('billing:quotaMeta.officialChannels.hint') } },
  get personalChannels() { return { label: i18n.t('billing:quotaMeta.personalChannels.label'), unit: i18n.t('billing:quotaMeta.personalChannels.unit'), short: i18n.t('billing:quotaMeta.personalChannels.short'), hint: i18n.t('billing:quotaMeta.personalChannels.hint') } },
}

interface PlanMeta {
  /** Dòng mở đầu danh sách tính năng, vd "Tất cả trong gói Cơ bản, thêm:". */
  featuresIntro?: string
  features: string[]
}

/**
 * DỰ PHÒNG cho server cũ chưa trả `features` / `featuresIntro` trong GET /billing/plans. Nguồn thật là bảng giá do
 * super-admin đặt (Platform → Bảng giá gói) — xem buildPlans.
 */
const PLAN_META: Record<string, PlanMeta> = {
  get free(): PlanMeta {
    return {
      features: [
        i18n.t('billing:planMeta.free.f1'),
        i18n.t('billing:planMeta.free.f2'),
        i18n.t('billing:planMeta.free.f3'),
        i18n.t('billing:planMeta.free.f4'),
      ],
    }
  },
  get starter(): PlanMeta {
    return {
      featuresIntro: i18n.t('billing:planMeta.starter.intro'),
      features: [
        i18n.t('billing:planMeta.starter.f1'),
        i18n.t('billing:planMeta.starter.f2'),
        i18n.t('billing:planMeta.starter.f3'),
        i18n.t('billing:planMeta.starter.f4'),
      ],
    }
  },
  get pro(): PlanMeta {
    return {
      featuresIntro: i18n.t('billing:planMeta.pro.intro'),
      features: [
        i18n.t('billing:planMeta.pro.f1'),
        i18n.t('billing:planMeta.pro.f2'),
        i18n.t('billing:planMeta.pro.f3'),
        i18n.t('billing:planMeta.pro.f4'),
        i18n.t('billing:planMeta.pro.f5'),
      ],
    }
  },
  get enterprise(): PlanMeta {
    return {
      featuresIntro: i18n.t('billing:planMeta.enterprise.intro'),
      features: [
        i18n.t('billing:planMeta.enterprise.f1'),
        i18n.t('billing:planMeta.enterprise.f2'),
        i18n.t('billing:planMeta.enterprise.f3'),
      ],
    }
  },
}

export interface PlanDef extends Omit<PricingPlan, keyof PlanMeta>, PlanMeta {}

/** Tính năng hiển thị: ưu tiên dữ liệu server (bảng giá), không có thì dự phòng theo mã gói. */
function metaOf(p: PricingPlan): PlanMeta {
  if (Array.isArray(p.features)) return { featuresIntro: p.featuresIntro?.trim() || undefined, features: p.features.map((f) => f.trim()).filter(Boolean) }
  return PLAN_META[p.id] ?? { features: [] }
}

/** Ghép bảng giá server với phần hiển thị. Gói super-admin thêm mới → không có danh sách tính năng. */
export function buildPlans(cfg: PricingConfig): PlanDef[] {
  return cfg.plans.filter((p) => !p.hidden).map((p) => ({ ...p, ...metaOf(p) }))
}

const ALIASES: Record<string, string> = {
  free: 'free', 'mien phi': 'free', trial: 'free', 'dung thu': 'free',
  starter: 'starter', basic: 'starter', 'co ban': 'starter',
  pro: 'pro', professional: 'pro', 'chuyen nghiep': 'pro',
  enterprise: 'enterprise', business: 'enterprise', 'doanh nghiep': 'enterprise', custom: 'enterprise',
}
const DEFAULT_NAMES: Record<string, string> = {
  get free() { return i18n.t('billing:planNames.free') },
  get starter() { return i18n.t('billing:planNames.starter') },
  get pro() { return i18n.t('billing:planNames.pro') },
  get enterprise() { return i18n.t('billing:planNames.enterprise') },
}

/** Chuẩn hoá nhãn: thường hoá, bỏ dấu, bỏ tiền tố "gói", gộp khoảng trắng. */
export function normalizePlanLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^goi /, '')
}

export type ResolvedPlan = { kind: 'known'; id: string; name: string } | { kind: 'custom'; id: null; name: string }

/** Nhãn gói của tổ chức → gói (xem bảng ánh xạ ở đầu file). `plans` cấp tên theo bảng giá. */
export function resolvePlan(label: string | null | undefined, plans: PricingPlan[] = []): ResolvedPlan {
  const raw = label?.trim() ?? ''
  const norm = raw ? normalizePlanLabel(raw) : 'free'
  const id = ALIASES[norm] ?? (plans.some((p) => p.id === norm) ? norm : undefined)
  if (!id) return { kind: 'custom', id: null, name: raw }
  return { kind: 'known', id, name: plans.find((p) => p.id === id)?.name ?? DEFAULT_NAMES[id] ?? id }
}

/**
 * Thứ hạng gói — CÙNG quy tắc với server (billing/purchase-policy.ts): theo giá / tháng, gói "Liên hệ"
 * (giá null) cao nhất, cùng giá → theo thứ tự trong bảng giá. null = không có trong bảng giá.
 */
type RankedPlan = Pick<PricingPlan, 'id' | 'monthlyPrice'>

export function planRank(plans: RankedPlan[], id: string | null | undefined): [number, number] | null {
  const idx = id ? plans.findIndex((p) => p.id === id) : -1
  if (idx < 0) return null
  const price = plans[idx].monthlyPrice
  return [price == null ? Number.POSITIVE_INFINITY : price, idx]
}

/** -1 a thấp hơn b, 0 cùng gói, 1 a cao hơn b; null = không so được. */
export function comparePlans(plans: RankedPlan[], a: string, b: string): -1 | 0 | 1 | null {
  if (a === b) return 0
  const ra = planRank(plans, a)
  const rb = planRank(plans, b)
  if (!ra || !rb) return null
  if (ra[0] !== rb[0]) return ra[0] < rb[0] ? -1 : 1
  return ra[1] < rb[1] ? -1 : 1
}

/** Gói `target` có cao hơn gói hiện tại không (gói tuỳ chỉnh coi như cao nhất). */
export function isUpgrade(current: ResolvedPlan, target: string, plans: PricingPlan[]): boolean {
  if (current.kind === 'custom') return false
  return comparePlans(plans, target, current.id) === 1
}

/**
 * "Mua rồi không trả lại": khi gói trả phí còn hạn (`summary.upgradeOnly`) không mua được gói thấp hơn.
 * Gói tuỳ chỉnh / chưa so được → không chặn (server là nguồn sự thật).
 */
export function isBlockedDowngrade(upgradeOnly: boolean, current: ResolvedPlan, target: string, plans: PricingPlan[]): boolean {
  if (!upgradeOnly || current.kind === 'custom') return false
  return comparePlans(plans, target, current.id) === -1
}

export function downgradeNote(): string {
  return i18n.t('billing:plans.downgradeNote')
}

/**
 * Số lượng tối thiểu khi mua `target` lúc gói trả phí còn hạn: không thấp hơn số ĐANG HIỆU LỰC (đã mua +
 * mua thêm; không có → số gồm sẵn của gói đang dùng), kẹp theo tối đa của gói mới. null = không ràng buộc
 * (free / hết hạn → mua mới tuỳ ý). Khớp purchase-policy.minimumQuantities phía server.
 */
export function purchaseFloor(
  target: PricingPlan,
  opts: { upgradeOnly: boolean; currentPlan?: PricingPlan | null; purchased?: Quotas | null },
): Quotas | null {
  if (!opts.upgradeOnly || !opts.currentPlan) return null
  const cur = opts.purchased ?? opts.currentPlan.included
  const out = {} as Quotas
  for (const k of QUOTA_KEYS) {
    const mx = target.max[k]
    out[k] = Math.max(target.included[k], mx == null ? cur[k] : Math.min(cur[k], mx))
  }
  return out
}

/** % giảm lớn nhất trong các mốc thời hạn (nhãn "Tiết kiệm đến …"). */
export function maxDiscount(cfg: PricingConfig): number {
  return cfg.durations.reduce((m, d) => Math.max(m, d.discountPercent), 0)
}

/** Nhãn góc của một mốc thời hạn: nhãn riêng (vd "Dùng thử") hoặc "−10%". */
export function durationBadge(d: { discountPercent: number; badge: string }): string {
  if (d.badge) return d.badge
  return d.discountPercent > 0 ? `−${d.discountPercent}%` : ''
}

/**
 * Số lượng điền sẵn khi mở trang Thay đổi gói: không thấp hơn số gồm sẵn, giữ số đã mua
 * (cùng gói), số tối thiểu khi gói còn hạn (`floor`, xem purchaseFloor) và số đang dùng thực tế;
 * kẹp theo tối đa của gói.
 */
export function initialQuantities(
  plan: PricingPlan,
  opts: { purchased?: Quotas | null; samePlan?: boolean; usage?: Partial<Quotas>; floor?: Quotas | null } = {},
): Quotas {
  const out = {} as Quotas
  for (const k of QUOTA_KEYS) {
    let q = plan.included[k]
    if (opts.samePlan && opts.purchased) q = Math.max(q, opts.purchased[k])
    if (opts.floor) q = Math.max(q, opts.floor[k])
    if (opts.usage?.[k] != null) q = Math.max(q, opts.usage[k]!)
    const mx = plan.max[k]
    out[k] = mx != null ? Math.min(q, mx) : q
  }
  return out
}

/** Ước tính ngày hết hạn sau kích hoạt (server tính thật): gia hạn cùng gói còn hạn thì nối tiếp. */
export function estimatePeriodEnd(opts: { days: number; sameActive: boolean; expiresAt: string | null; now?: Date }): Date {
  const now = opts.now ?? new Date()
  const start = opts.sameActive && opts.expiresAt ? new Date(opts.expiresAt) : now
  return new Date(start.getTime() + opts.days * 24 * 60 * 60 * 1000)
}

/**
 * Ước tính ngày hết hạn sau kích hoạt theo báo giá: gia hạn nối tiếp (mode 'extend') thì từ ngày hết hạn;
 * đổi số lượng / đổi gói / có quy đổi thì từ hôm nay + tổng số ngày (gồm ngày quy đổi dư). Chưa có báo giá →
 * đoán theo cùng gói còn hạn.
 */
export function estimateActivationEnd(opts: {
  quote?: Pick<Quote, 'mode' | 'credit' | 'totalDays'> | null
  days: number
  sameActive: boolean
  expiresAt: string | null
  now?: Date
}): Date {
  const now = opts.now ?? new Date()
  const q = opts.quote
  if (q && (q.credit || q.mode === 'requantity' || q.mode === 'change_plan' || q.mode === 'new')) {
    return new Date(now.getTime() + (q.totalDays || opts.days) * 24 * 60 * 60 * 1000)
  }
  return estimatePeriodEnd({ days: opts.days, sameActive: q?.mode === 'extend' || (!q?.mode && opts.sameActive), expiresAt: opts.expiresAt, now })
}

/** Giải thích cách đơn áp vào gói đang dùng (trang Thay đổi gói). null = không cần giải thích. */
export function purchaseModeNote(mode: PurchaseMode | undefined): { title: string; text: string } | null {
  switch (mode) {
    case 'extend':
      return {
        title: i18n.t('billing:purchaseModeNote.extend.title'),
        text: i18n.t('billing:purchaseModeNote.extend.text'),
      }
    case 'requantity':
      return {
        title: i18n.t('billing:purchaseModeNote.requantity.title'),
        text: i18n.t('billing:purchaseModeNote.requantity.text'),
      }
    case 'change_plan':
      return {
        title: i18n.t('billing:purchaseModeNote.changePlan.title'),
        text: i18n.t('billing:purchaseModeNote.changePlan.text'),
      }
    default:
      return null
  }
}

/** Đơn "Mua thêm" cho kỳ hiện tại. */
export function isAddonOrder(o: Pick<BillingOrder, 'cycle'> & { quote?: Pick<Quote, 'kind'> | null }): boolean {
  return o.cycle === 'addon' || o.quote?.kind === 'addon'
}

/** Đơn mua credit AI (billing_orders loại ai_credits). */
export function isAiCreditOrder(o: Pick<BillingOrder, 'cycle'> & { planId?: string | null; quote?: Pick<Quote, 'kind'> | null }): boolean {
  return o.cycle === 'ai_credits' || o.quote?.kind === 'ai_credits' || o.planId === 'ai_credits'
}

/** Đơn vị nạp (nội bộ) của đơn mua lượt AI (null nếu server không gửi). Không hiện cho khách — dùng aiCreditOrderLabel. */
export function aiCreditOrderCredits(o: { quote?: Pick<Quote, 'aiCredits'> | null }): number | null {
  return o.quote?.aiCredits?.credits ?? null
}

const dong = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`

/** Nhãn đơn mua lượt AI: "10.000 lượt AI · 500.000đ" (đơn cũ chưa có số lượt: "Gói 500.000đ"). Thiếu giá → null. */
export function aiCreditOrderLabel(o: { quote?: Pick<Quote, 'aiCredits'> | null }): string | null {
  const c = o.quote?.aiCredits
  if (!c?.price) return null
  return c.turns
    ? i18n.t('billing:plans.aiCreditLabel', { turns: new Intl.NumberFormat('vi-VN').format(c.turns), price: dong(c.price) })
    : i18n.t('billing:plans.aiCreditLabelNoTurns', { price: dong(c.price) })
}

/** "+2 nhân viên, +1 kênh chính thức" — phần tăng của đơn mua thêm. */
export function addonDeltaText(delta: Quotas | null | undefined): string {
  if (!delta) return ''
  return QUOTA_KEYS.filter((k) => delta[k] > 0).map((k) => `+${delta[k]} ${QUOTA_META[k].short}`).join(', ')
}

/** Nhãn thời hạn của đơn trong lịch sử. */
export function termLabel(days: number): string {
  return i18n.t('billing:plans.termLabel', { days })
}

/** Số ngày mua tối đa — khớp MAX_DAYS phía server (billing/pricing.ts). */
export const MAX_DAYS = 1100

/** Số ngày tối thiểu = mốc thời hạn nhỏ nhất. */
export function minDays(cfg: PricingConfig): number {
  return cfg.durations.reduce((m, d) => Math.min(m, d.days), Infinity)
}

/** % giảm áp cho số ngày nhập: theo mốc lớn nhất ≤ số ngày (cùng quy tắc server). null = ngoài khoảng. */
export function discountForDays(cfg: PricingConfig, days: number): number | null {
  if (!Number.isInteger(days) || days < minDays(cfg) || days > MAX_DAYS) return null
  let pct = 0
  let best = -1
  for (const d of cfg.durations) if (d.days <= days && d.days > best) { best = d.days; pct = d.discountPercent }
  return pct
}

/** Mốc giảm giá kế tiếp (để gợi ý "mua thêm N ngày giảm X%"). */
export function nextDiscountTier(cfg: PricingConfig, days: number): { days: number; discountPercent: number } | null {
  const cur = discountForDays(cfg, days) ?? 0
  const next = [...cfg.durations].sort((a, b) => a.days - b.days).find((d) => d.days > days && d.discountPercent > cur)
  return next ? { days: next.days, discountPercent: next.discountPercent } : null
}

// ── Mã ưu đãi ───────────────────────────────────────────────────────

/** Đơn 0đ nhờ quy đổi gói cũ / mã ưu đãi → kích hoạt ngay, không qua cổng thanh toán. */
export function isFreeOrder(q: Pick<Quote, 'total' | 'credit' | 'promo'> | undefined): boolean {
  return !!q && q.total === 0 && (!!q.credit || !!q.promo)
}

/** Chuẩn hoá mã khách nhập: bỏ khoảng trắng, chữ hoa, tối đa 32 ký tự. */
export function normalizePromoInput(s: string): string {
  return s.replace(/\s/g, '').toUpperCase().slice(0, 32)
}

/**
 * Chặn "Đặt hàng" vì mã: đang nhập mã mà mã lỗi / không áp được. Mã không cộng dồn mà giảm theo thời hạn lợi hơn
 * (quote.promoNote, A3) KHÔNG chặn — đặt hàng không kèm mã.
 */
export function isPromoBlocked(promoCode: string, q: Pick<Quote, 'promo' | 'promoError' | 'promoNote'> | undefined): boolean {
  if (!promoCode || !q) return false
  return !!q.promoError || (!q.promo && !q.promoNote)
}

/** B9: bỏ `promo` khỏi tham số URL (sau khi đơn đã trả). null = không có gì để bỏ. */
export function withoutPromoParam(params: URLSearchParams): URLSearchParams | null {
  if (!params.has('promo')) return null
  const n = new URLSearchParams(params)
  n.delete('promo')
  return n
}

// ── Số lượng: không dưới số đang dùng (B8) ────────────────────────────

/** Số đang dùng theo tóm tắt /billing → Quotas. */
export function usageQuotas(usage: { members: number; channelsOfficial: number; channelsPersonal: number }): Quotas {
  return { members: usage.members, officialChannels: usage.channelsOfficial, personalChannels: usage.channelsPersonal }
}

/**
 * Giới hạn ô chọn số lượng: tối thiểu = max(số gồm sẵn, số tối thiểu khi gói còn hạn / số đang có khi mua thêm,
 * số ĐANG DÙNG thực tế) — không cho chọn dưới số đang dùng (server cũng chặn: 400 QUANTITY_BELOW_USAGE).
 * `overMax`: số đang dùng vượt mức tối đa của gói → không mua được gói này với số đang dùng.
 */
export function stepperBounds(
  plan: Pick<PricingPlan, 'included' | 'max'>,
  k: QuotaKey,
  opts: { floor?: number; used?: number; floorLabel?: string } = {},
): { min: number; max: number | null; label: string; overMax: boolean } {
  const base = Math.max(plan.included[k], opts.floor ?? 0)
  const used = Math.max(0, opts.used ?? 0)
  const mx = plan.max[k]
  const overMax = mx != null && used > mx
  const min = Math.max(base, mx != null ? Math.min(used, mx) : used)
  const label = used > base ? i18n.t('billing:plans.inUse') : (opts.floorLabel ?? i18n.t('billing:plans.included'))
  return { min, max: mx, label, overMax }
}

/** Số lượng đang chọn còn thấp hơn số đang dùng ở mục nào không (để khoá nút đặt hàng). */
export function belowUsage(qty: Quotas | null, usage: Quotas): QuotaKey[] {
  if (!qty) return []
  return QUOTA_KEYS.filter((k) => qty[k] < usage[k])
}

// ── Đổi số lượng cùng gói (B4 / B5) ───────────────────────────────────

/**
 * B4: cảnh báo khi đổi số lượng cùng gói — phần còn lại của kỳ đang dùng được quy đổi theo ĐƠN GIÁ ĐƠN MỚI
 * (kỳ dài đang được giảm sâu đổi sang đơn ngắn hơn thì mất lợi ích giảm giá dài hạn). null = không cần cảnh báo.
 */
export function requantityWarning(q: Pick<Quote, 'mode' | 'credit' | 'days'> | undefined): string | null {
  if (!q || q.mode !== 'requantity' || !q.credit) return null
  if (q.days < q.credit.remainingDays) {
    return i18n.t('billing:plans.requantityWarningShort', { remainingDays: q.credit.remainingDays, days: q.days })
  }
  return i18n.t('billing:plans.requantityWarningNormal')
}

// ── Số ngày còn lại (Q3) ─────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Số ngày còn lại tới `end` — Q3 (29/09/2026): làm tròn LÊN ở MỌI nơi, tối thiểu 1 khi còn hạn; đã qua / không có → 0.
 * CÙNG quy tắc với server (billing/pricing.ts daysLeft — số ngày tính tiền "Mua thêm"), để số hiển thị không lệch số tính tiền.
 */
export function daysLeft(end: Date | string | number | null | undefined, now: Date = new Date()): number {
  if (end == null) return 0
  const t = end instanceof Date ? end.getTime() : new Date(end).getTime()
  if (!Number.isFinite(t)) return 0
  const ms = t - now.getTime()
  return ms > 0 ? Math.max(1, Math.ceil(ms / DAY_MS)) : 0
}

// ── VAT (Q2) ─────────────────────────────────────────────────────────

/** Thuế suất mặc định khi server chưa trả `vatPercent` (khớp DEFAULT_VAT_PERCENT phía server). */
export const DEFAULT_VAT_PERCENT = 10

export function vatPercentOf(cfg: Pick<PricingConfig, 'vatPercent'> | null | undefined): number {
  const v = cfg?.vatPercent
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : DEFAULT_VAT_PERCENT
}

/** Ghi chú giá niêm yết (thay "Giá chưa gồm VAT"). */
export function vatListNote(percent: number): string {
  return percent > 0
    ? i18n.t('billing:plans.vatListNote', { percent })
    : i18n.t('billing:plans.vatListNoteZero')
}

/** Nhãn dòng VAT: "VAT 10%" / "VAT (không chịu thuế)". */
export function vatLabel(percent: number): string {
  return percent > 0 ? i18n.t('billing:plans.vatLabel', { percent }) : i18n.t('billing:plans.vatLabelZero')
}

export interface VatBreakdown { percent: number; preTax: number; amount: number; total: number }

/**
 * Tách tiền của báo giá / đơn để hiện "Tạm tính (chưa VAT)", "VAT x%", "Tổng thanh toán".
 * `amount` = số tiền đơn (order.amount) nếu có. Đơn cũ (không có quote.vat) → null: chỉ hiện tổng đã trả.
 */
export function vatBreakdown(
  q: { vat?: { percent: number; preTax: number; amount: number } | null; total?: number } | null | undefined,
  amount?: number,
): VatBreakdown | null {
  const v = q?.vat
  if (!v) return null
  return { percent: v.percent, preTax: v.preTax, amount: v.amount, total: amount ?? q?.total ?? v.preTax + v.amount }
}
